import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FileText, Handshake, Loader2, Pencil, Plus, RotateCcw, Wallet, XCircle } from 'lucide-react';
import { PartnerStatementDialog } from '@/components/referrals/PartnerStatementDialog';
import { toast } from 'sonner';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useLeadSources } from '@/hooks/useReferenceData';
import {
  FEE_BASIS_LABEL,
  PAY_WHEN_LABEL,
  describeFeeRule,
  describeVoidReason,
  useRecordReferralPayout,
  useReferralFees,
  useReferralPartnerTotals,
  useReferralPartners,
  useReferralPayouts,
  useRestoreReferralFee,
  useSaveReferralPartner,
  useVoidReferralFee,
  type FeeBasis,
  type FeeStatus,
  type PayWhen,
  type ReferralFeeRow,
  type ReferralPartner,
  type ReferralPartnerInput,
} from '@/hooks/useReferralPartners';

const NEW_SOURCE = '__new__';
const FEE_BASES: FeeBasis[] = ['per_lead', 'per_job', 'percent_revenue', 'percent_gross_profit', 'percent_profit'];
const PAY_WHENS: PayWhen[] = ['job_paid', 'job_completed'];

type LedgerTab = 'payable' | 'accrued' | 'paid' | 'void';
const LEDGER_TABS: { value: LedgerTab; label: string }[] = [
  { value: 'payable', label: 'Owed now' },
  { value: 'accrued', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'void', label: 'Voided' },
];

const STATUS_BADGE: Record<FeeStatus, { label: string; className: string }> = {
  accrued: { label: 'Pending', className: 'bg-info/10 text-info border-info/20' },
  payable: { label: 'Owed', className: 'bg-warning/10 text-warning border-warning/20' },
  paid: { label: 'Paid', className: 'bg-success/10 text-success border-success/20' },
  void: { label: 'Void', className: 'bg-muted text-muted-foreground' },
};

function todayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

interface PartnerForm {
  name: string;
  lead_source_id: string; // NEW_SOURCE = create one named after the partner
  fee_basis: FeeBasis;
  fee_amount: string;
  fee_percent: string;
  pay_when: PayWhen;
  fee_start_date: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  notes: string;
  is_active: boolean;
}

const EMPTY_FORM: PartnerForm = {
  name: '',
  lead_source_id: NEW_SOURCE,
  fee_basis: 'per_lead',
  fee_amount: '',
  fee_percent: '',
  pay_when: 'job_paid',
  fee_start_date: todayIso(),
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  notes: '',
  is_active: true,
};

function formFromPartner(p: ReferralPartner): PartnerForm {
  return {
    name: p.name,
    lead_source_id: p.lead_source_id ?? NEW_SOURCE,
    fee_basis: p.fee_basis,
    fee_amount: p.fee_amount ? String(p.fee_amount) : '',
    fee_percent: p.fee_percent ? String(p.fee_percent) : '',
    pay_when: p.pay_when,
    fee_start_date: p.fee_start_date,
    contact_name: p.contact_name ?? '',
    contact_email: p.contact_email ?? '',
    contact_phone: p.contact_phone ?? '',
    notes: p.notes ?? '',
    is_active: p.is_active,
  };
}

export function ReferralPartners() {
  const { locale, formatMoney, formatShortDate } = useCompanyLocale();
  const { data: partners = [], isLoading } = useReferralPartners();
  const { data: totals = {} } = useReferralPartnerTotals();
  const { data: leadSources = [] } = useLeadSources(false);
  const save = useSaveReferralPartner();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ReferralPartner | null>(null);
  const [form, setForm] = useState<PartnerForm>(EMPTY_FORM);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selected = partners.find((p) => p.id === selectedId) ?? null;

  // Sources already owned by another partner cannot be picked twice.
  const takenSources = useMemo(() => {
    const s = new Set<string>();
    for (const p of partners) if (p.lead_source_id && p.id !== editing?.id) s.add(p.lead_source_id);
    return s;
  }, [partners, editing]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, fee_start_date: todayIso() });
    setDialogOpen(true);
  };
  const openEdit = (p: ReferralPartner) => {
    setEditing(p);
    setForm(formFromPartner(p));
    setDialogOpen(true);
  };

  const isPercent = form.fee_basis.startsWith('percent_');

  const handleSave = async () => {
    const name = form.name.trim();
    if (!name) return toast.error('Give the partner a name');
    const amount = parseFloat(form.fee_amount || '0');
    const percent = parseFloat(form.fee_percent || '0');
    if (isPercent && (!(percent > 0) || percent > 100)) return toast.error('Enter a percentage between 0 and 100');
    if (!isPercent && !(amount >= 0)) return toast.error('Enter a fee amount');
    if (!form.fee_start_date) return toast.error('Pick the date fees start from');

    const input: ReferralPartnerInput = {
      name,
      lead_source_id: form.lead_source_id === NEW_SOURCE ? null : form.lead_source_id,
      fee_basis: form.fee_basis,
      fee_amount: isPercent ? 0 : Math.round(amount * 100) / 100,
      fee_percent: isPercent ? Math.round(percent * 100) / 100 : 0,
      pay_when: form.pay_when,
      fee_start_date: form.fee_start_date,
      contact_name: form.contact_name.trim() || null,
      contact_email: form.contact_email.trim() || null,
      contact_phone: form.contact_phone.trim() || null,
      notes: form.notes.trim() || null,
      is_active: form.is_active,
    };
    try {
      const id = await save.mutateAsync({ id: editing?.id, input });
      toast.success(editing ? 'Partner updated' : 'Partner added');
      setDialogOpen(false);
      setSelectedId(id);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not save the partner';
      toast.error(msg.includes('referral_partners_company_source_uq') ? 'That lead source already belongs to another partner' : msg);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">Referral partners</h3>
          <p className="text-sm text-muted-foreground">
            Outside companies that send you leads and are paid a fee for them. Each partner is a lead source: leads
            tagged with it (directly, or through one of its campaigns) accrue the fee automatically as they turn into
            completed and paid jobs. Record payouts here when you settle up.
          </p>
        </div>
        <Button onClick={openCreate} className="shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          Add partner
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : partners.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-6 py-10 text-center text-sm text-muted-foreground">
              <Handshake className="h-8 w-8 opacity-40" />
              <p>No referral partners yet. Add the companies that send you work and what you pay them per lead or per job.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Partner</TableHead>
                  <TableHead>Lead source</TableHead>
                  <TableHead>Fee rule</TableHead>
                  <TableHead className="text-right">Owed now</TableHead>
                  <TableHead className="text-right">Pending</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[1%]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {partners.map((p) => {
                  const t = totals[p.id];
                  const active = p.id === selectedId;
                  return (
                    <TableRow
                      key={p.id}
                      className={active ? 'bg-primary/5' : 'cursor-pointer'}
                      onClick={() => setSelectedId(p.id)}
                    >
                      <TableCell>
                        <div className="font-medium">{p.name}</div>
                        {(p.contact_name || p.contact_email) && (
                          <div className="text-xs text-muted-foreground">
                            {[p.contact_name, p.contact_email].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{p.lead_sources?.name ?? '—'}</TableCell>
                      <TableCell className="text-sm">{describeFeeRule(p, formatMoney)}</TableCell>
                      <TableCell className="text-right font-medium tabular-nums">{formatMoney(t?.payable ?? 0)}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{formatMoney(t?.accrued ?? 0)}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{formatMoney(t?.paid ?? 0)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={p.is_active ? 'bg-success/10 text-success border-success/20' : 'bg-muted text-muted-foreground'}>
                          {p.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Edit ${p.name}`}
                          onClick={(e) => { e.stopPropagation(); openEdit(p); }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {selected && <PartnerLedger partner={selected} />}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit partner' : 'Add referral partner'}</DialogTitle>
            <DialogDescription>
              The fee rule applies to every lead from this partner from the start date on. Changing it re-prices fees not yet paid.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <Label htmlFor="rp-name">Company name</Label>
              <Input id="rp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. HomeLink Referrals" />
            </div>

            <div className="grid gap-1.5">
              <Label>Lead source</Label>
              <Select value={form.lead_source_id} onValueChange={(v) => setForm({ ...form, lead_source_id: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {!editing && <SelectItem value={NEW_SOURCE}>Create a new source named after the partner</SelectItem>}
                  {leadSources.map((s) => (
                    <SelectItem key={s.id} value={s.id} disabled={takenSources.has(s.id)}>
                      {s.name}{!s.is_active ? ' (inactive)' : ''}{takenSources.has(s.id) ? ' — another partner' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Leads with this source, or with a campaign under it, count as this partner's.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>Fee</Label>
                <Select value={form.fee_basis} onValueChange={(v) => setForm({ ...form, fee_basis: v as FeeBasis })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FEE_BASES.map((b) => <SelectItem key={b} value={b}>{FEE_BASIS_LABEL[b]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="rp-rate">{isPercent ? 'Percentage' : `Amount (${locale.currency})`}</Label>
                {isPercent ? (
                  <div className="relative">
                    <Input id="rp-rate" type="number" min={0} max={100} step="0.5" value={form.fee_percent}
                      onChange={(e) => setForm({ ...form, fee_percent: e.target.value })} className="pr-7" />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                  </div>
                ) : (
                  <Input id="rp-rate" type="number" min={0} step="0.01" value={form.fee_amount}
                    onChange={(e) => setForm({ ...form, fee_amount: e.target.value })} />
                )}
              </div>
            </div>

            {form.fee_basis !== 'per_lead' && (
              <div className="grid gap-1.5">
                <Label>Becomes owed</Label>
                <Select value={form.pay_when} onValueChange={(v) => setForm({ ...form, pay_when: v as PayWhen })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAY_WHENS.map((w) => <SelectItem key={w} value={w}>{PAY_WHEN_LABEL[w]}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Until then the fee shows as pending. A cancelled job owes nothing.
                </p>
              </div>
            )}

            <div className="grid gap-1.5">
              <Label htmlFor="rp-start">Fees start from</Label>
              <Input id="rp-start" type="date" value={form.fee_start_date} onChange={(e) => setForm({ ...form, fee_start_date: e.target.value })} />
              <p className="text-xs text-muted-foreground">
                Leads created before this date never accrue a fee. Set it back to pick up history you still owe for.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="rp-cname">Contact</Label>
                <Input id="rp-cname" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="rp-cemail">Email</Label>
                <Input id="rp-cemail" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="rp-cphone">Phone</Label>
                <Input id="rp-cphone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
              </div>
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="rp-notes">Notes</Label>
              <Textarea id="rp-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Payment terms, who to invoice, agreement date…" />
            </div>

            {editing && (
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <div className="text-sm font-medium">Active</div>
                  <div className="text-xs text-muted-foreground">Inactive partners accrue no new fees. What is already owed stays owed.</div>
                </div>
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={save.isPending}>
              {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editing ? 'Save' : 'Add partner'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ledger for one partner: fees by status, payouts, record a payout
// ---------------------------------------------------------------------------
function PartnerLedger({ partner }: { partner: ReferralPartner }) {
  const { formatMoney, formatShortDate } = useCompanyLocale();
  const [tab, setTab] = useState<LedgerTab>('payable');
  const { data: fees = [], isLoading } = useReferralFees(partner.id, [tab]);
  const { data: payouts = [] } = useReferralPayouts(partner.id);
  const voidFee = useVoidReferralFee();
  const restoreFee = useRestoreReferralFee();
  const recordPayout = useRecordReferralPayout();

  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [statementOpen, setStatementOpen] = useState(false);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [paidOn, setPaidOn] = useState(todayIso());
  const [method, setMethod] = useState('');
  const [reference, setReference] = useState('');
  const [payoutNotes, setPayoutNotes] = useState('');

  const payableFees = tab === 'payable' ? fees : [];
  const selectedFees = payableFees.filter((f) => checked.has(f.id));
  const selectedTotal = selectedFees.reduce((s, f) => s + Number(f.amount), 0);
  const allChecked = payableFees.length > 0 && selectedFees.length === payableFees.length;

  const switchTab = (t: LedgerTab) => { setTab(t); setChecked(new Set()); };
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(payableFees.map((f) => f.id)));
  const toggleOne = (id: string) => {
    const next = new Set(checked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setChecked(next);
  };

  const openPayout = () => {
    setPaidOn(todayIso()); setMethod(''); setReference(''); setPayoutNotes('');
    setPayoutOpen(true);
  };

  const submitPayout = async () => {
    if (selectedFees.length === 0) return;
    try {
      const r = await recordPayout.mutateAsync({
        partnerId: partner.id,
        feeIds: selectedFees.map((f) => f.id),
        paidOn,
        method: method.trim() || null,
        reference: reference.trim() || null,
        notes: payoutNotes.trim() || null,
      });
      toast.success(`Payout of ${formatMoney(Number(r.amount))} recorded for ${r.fee_count} fee${r.fee_count === 1 ? '' : 's'}`);
      setPayoutOpen(false);
      setChecked(new Set());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not record the payout');
    }
  };

  const onVoid = async (f: ReferralFeeRow) => {
    const reason = window.prompt('Why is this fee not owed? (optional)') ?? null;
    if (reason === null) return;
    try {
      await voidFee.mutateAsync({ feeId: f.id, reason: reason.trim() || null });
      toast.success('Fee voided');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not void the fee');
    }
  };

  const onRestore = async (f: ReferralFeeRow) => {
    try {
      await restoreFee.mutateAsync(f.id);
      toast.success('Fee restored; it will be re-priced from the job');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not restore the fee');
    }
  };

  const describeBasis = (f: ReferralFeeRow) => {
    if (f.fee_basis === 'per_lead') return 'Per lead';
    if (f.fee_basis === 'per_job') return 'Per job';
    const base = f.base_amount == null ? '—' : formatMoney(Number(f.base_amount));
    const what = f.fee_basis === 'percent_revenue' ? 'revenue' : f.fee_basis === 'percent_gross_profit' ? 'profit before tech' : 'company profit';
    return `${Number(f.rate)}% of ${base} ${what}`;
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base">{partner.name}</CardTitle>
              <CardDescription>
                {describeFeeRule(partner, formatMoney)} · fees from {formatShortDate(partner.fee_start_date)}
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setStatementOpen(true)}>
                <FileText className="mr-2 h-4 w-4" />
                Statement
              </Button>
              {tab === 'payable' && (
                <Button onClick={openPayout} disabled={selectedFees.length === 0}>
                  <Wallet className="mr-2 h-4 w-4" />
                  Record payout{selectedFees.length > 0 ? ` · ${formatMoney(selectedTotal)}` : ''}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div role="tablist" aria-label="Fee status" className="flex gap-1 border-b">
            {LEDGER_TABS.map((t) => (
              <button
                key={t.value}
                type="button"
                role="tab"
                aria-selected={tab === t.value}
                onClick={() => switchTab(t.value)}
                className={
                  tab === t.value
                    ? 'border-b-2 border-primary px-3 py-2 text-sm font-medium text-primary'
                    : 'border-b-2 border-transparent px-3 py-2 text-sm text-muted-foreground hover:text-foreground'
                }
              >
                {t.label}
              </button>
            ))}
          </div>

          {isLoading ? (
            <div className="space-y-2"><Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-full" /></div>
          ) : fees.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {tab === 'payable' && 'Nothing owed right now.'}
              {tab === 'accrued' && 'No fees waiting on a job to complete or be paid.'}
              {tab === 'paid' && 'No fees paid yet.'}
              {tab === 'void' && 'No voided fees.'}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {tab === 'payable' && (
                    <TableHead className="w-[1%]">
                      <Checkbox checked={allChecked} onCheckedChange={toggleAll} aria-label="Select all owed fees" />
                    </TableHead>
                  )}
                  <TableHead>Lead</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Basis</TableHead>
                  <TableHead className="text-right">Fee</TableHead>
                  <TableHead>{tab === 'paid' ? 'Paid' : tab === 'void' ? 'Reason' : 'Status'}</TableHead>
                  <TableHead className="w-[1%]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {fees.map((f) => (
                  <TableRow key={f.id}>
                    {tab === 'payable' && (
                      <TableCell>
                        <Checkbox checked={checked.has(f.id)} onCheckedChange={() => toggleOne(f.id)} aria-label={`Select ${f.leads?.lead_number ?? 'fee'}`} />
                      </TableCell>
                    )}
                    <TableCell>
                      <Link to={`/leads/${f.lead_id}`} className="font-medium text-primary hover:underline">
                        {f.leads?.lead_number ?? 'Lead'}
                      </Link>
                      <div className="text-xs text-muted-foreground">{formatShortDate(f.leads?.created_at ?? f.created_at)}</div>
                    </TableCell>
                    <TableCell className="text-sm">{f.leads?.customer_name ?? '—'}</TableCell>
                    <TableCell className="text-sm">
                      {f.job_id && f.jobs ? (
                        <>
                          <Link to={`/jobs/${f.job_id}`} className="text-primary hover:underline">{f.jobs.job_number}</Link>
                          <div className="text-xs text-muted-foreground">
                            {f.jobs.status}{f.jobs.status === 'completed' ? ` · ${f.jobs.payment_status.replace('_', ' ')}` : ''}
                          </div>
                        </>
                      ) : (
                        <span className="text-muted-foreground">No job yet</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{describeBasis(f)}</TableCell>
                    <TableCell className="text-right font-medium tabular-nums">{formatMoney(Number(f.amount))}</TableCell>
                    <TableCell className="text-sm">
                      {tab === 'paid' ? (
                        formatShortDate(f.paid_at)
                      ) : tab === 'void' ? (
                        <span className="text-muted-foreground">{describeVoidReason(f.void_reason)}</span>
                      ) : (
                        <Badge variant="outline" className={STATUS_BADGE[f.status].className}>{STATUS_BADGE[f.status].label}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {(f.status === 'accrued' || f.status === 'payable') && (
                        <Button variant="ghost" size="icon" aria-label="Void fee" title="Not owed (void)" onClick={() => onVoid(f)} disabled={voidFee.isPending}>
                          <XCircle className="h-4 w-4" />
                        </Button>
                      )}
                      {f.status === 'void' && (
                        <Button variant="ghost" size="icon" aria-label="Restore fee" title="Restore" onClick={() => onRestore(f)} disabled={restoreFee.isPending}>
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {payouts.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Payouts to {partner.name}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Paid on</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Fees</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payouts.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{formatShortDate(p.paid_on)}</TableCell>
                    <TableCell className="text-right font-medium tabular-nums">{formatMoney(Number(p.amount))}</TableCell>
                    <TableCell className="text-right tabular-nums">{p.fee_count}</TableCell>
                    <TableCell className="text-sm">{p.method ?? '—'}</TableCell>
                    <TableCell className="text-sm">{p.reference ?? '—'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{p.notes ?? ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <PartnerStatementDialog partner={partner} open={statementOpen} onOpenChange={setStatementOpen} />

      <Dialog open={payoutOpen} onOpenChange={setPayoutOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record payout to {partner.name}</DialogTitle>
            <DialogDescription>
              {selectedFees.length} fee{selectedFees.length === 1 ? '' : 's'} totalling {formatMoney(selectedTotal)} will be marked paid.
              This records a payment you made outside Steddy; it does not move money.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid gap-1.5">
              <Label htmlFor="po-date">Paid on</Label>
              <Input id="po-date" type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="po-method">Method</Label>
                <Input id="po-method" value={method} onChange={(e) => setMethod(e.target.value)} placeholder="Bank transfer" />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="po-ref">Reference</Label>
                <Input id="po-ref" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Their invoice no." />
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="po-notes">Notes</Label>
              <Textarea id="po-notes" rows={2} value={payoutNotes} onChange={(e) => setPayoutNotes(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayoutOpen(false)}>Cancel</Button>
            <Button onClick={submitPayout} disabled={recordPayout.isPending || !paidOn}>
              {recordPayout.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Mark {formatMoney(selectedTotal)} paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
